import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { TestimonialFeature } from '../components/Testimonials.jsx';
import { TPHero, TPWrittenWall, TPFilteredGrid, TPShareYourStory } from '../components/TestimonialsPage.jsx';
import { WrittenStoriesD } from '../components/WrittenStoriesD.jsx';
import { IntensiveCrossLink } from '../components/IntensiveCrossLink.jsx';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',

  tpKicker: 'Learner stories',
  tpTitle: "The proof isn't in the pudding.\nIt's in all the people\nspeaking their new language.",
  tpLede: 'In seventeen years, over a hundred thousand people have used the SaySomethingin Method to become more confident speakers of a wide range of languages. Here is a small corner of that: the rugby players and TV hosts, yes, but mostly the teachers, parents, students, grandparents and neighbours who decided it was time to start speaking.',
  tpStat1Num: '100,000+',
  tpStat1Label: 'learners across all courses',
  tpStat2Num: '4.2 / 5',
  tpStat2Label: 'Trustpilot rating',
  tpStat3Num: 'Unlimited mins',
  tpStat3Label: 'per day with every account',

  tpFeatureMoreLink: 'Read the BBC Radio 4 feature →',

  tpWallKicker: 'In their own words',
  tpWallTitle: 'Six voices, six different reasons.',
  tpWallQ1: "It's been outstanding, it's been absolutely brilliant. There's not many apps out there that make you laugh while you're learning.",
  tpWallN1: 'Scott Quinnell',
  tpWallR1: 'Rugby legend · SSi Welsh',
  tpWallQ2: "I was astonished at how quickly you can pick up useful Welsh. It's all very practical and happy and always encouraging.",
  tpWallN2: 'Carol Vorderman',
  tpWallR2: 'TV host & educator · SSi Welsh',
  tpWallQ3: "Learning with SaySomethinginWelsh has been wonderful… Aran is such an encouraging teacher… he's really positive.",
  tpWallN3: 'Rakie Ayola',
  tpWallR3: 'Actor · SSi Welsh',
  tpWallQ4: "SSi is an excellent way to build confidence. It gets you speaking and really works! I'm very grateful to them.",
  tpWallN4: 'Leanne Wood',
  tpWallR4: 'Former Leader of Plaid Cymru · SSi Welsh',
  tpWallQ5: "I'm on a four-month trip to Central America and about 95% of the Spanish I'm using here came from SSi.",
  tpWallN5: 'Rachel Healy',
  tpWallR5: 'Learner · SSi Spanish · Costa Rica',
  tpWallQ6: 'The SSi approach flies in the face of established language learning theory and method – and yet, it works.',
  tpWallN6: 'Prof. Tess Fitzpatrick',
  tpWallR6: 'Head of Applied Linguistics · Swansea University',

  emmaQuote: "I wish I could jump over there and give you all a big kiss 💋 THANKYOU soooo much! Honestly LOVE the App and can't wait to pack up my campervan, twins and puppy dog to go travelling soon too! Doing a little happy dance!",
  emmaName: 'Emma',
  emmaRole: 'Learner · SSi French',

  tpGridKicker: 'Video stories',
  tpGridTitle: 'Watch someone else do it first.',
  tpGridSub: 'Every clip is an unprompted, one-take testimonial – no script, no fee, just what they wanted to say.',
  tpFilterAll: 'All',
  tpFilterWelsh: 'Welsh',
  tpFilterSpanish: 'Spanish',
  tpGroupCelebs: 'Public voices',
  tpGroupLearners: 'Learners',
  tpEmpty: "Nothing to show for that filter yet – we're adding more every month.",

  tpShareKicker: 'Your turn',
  tpShareTitle: "Got a story? We'd love to share it.",
  tpShareBody: "If the course has changed something for you – a job interview, a chat with a grandparent, a weekend in the valleys – we'd be thrilled to hear it. Just a short video or a few paragraphs, and you'll be helping inspire the next generation of learners.",
  tpShareCta1: 'Email your story',
  tpShareCta2: 'Rather write to us first',
  tpShareStep1Title: 'Record or write',
  tpShareStep1Body: '60 seconds on your phone, or a short email. Welsh or English, either is brilliant.',
  tpShareStep2Title: 'Send it to stories@',
  tpShareStep2Body: "We'll read every one – usually within a day – and reply personally.",
  tpShareStep3Title: "We'll ask before we publish",
  tpShareStep3Body: 'Nothing goes up without your explicit say-so. You can say yes, no, or yes-but-no-photo.',

  testiKicker: 'A word from Ruth Jones',
  testiQuote: "It's absolutely brilliant, it's really good fun, it's really easy to use – I don't know how it works but it does. I can't recommend it highly enough.",
  testiName: 'Ruth Jones',
  testiRole: 'Actor & writer · SSi Welsh graduate',
  testiCredit: 'As seen on BBC Radio 4',
  testiMoreLink: 'Watch more learner stories ↓',

  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Hero', keys: ['tpKicker','tpTitle','tpLede','tpStat1Num','tpStat1Label','tpStat2Num','tpStat2Label','tpStat3Num','tpStat3Label'], long:['tpTitle','tpLede'] },
  { name: 'Featured (Ruth Jones)', keys: ['testiKicker','testiQuote','testiName','testiRole','testiCredit','testiMoreLink'], long:['testiQuote'] },
  { name: 'Quote wall', keys: ['tpWallKicker','tpWallTitle','tpWallQ1','tpWallN1','tpWallR1','tpWallQ2','tpWallN2','tpWallR2','tpWallQ3','tpWallN3','tpWallR3','tpWallQ4','tpWallN4','tpWallR4','tpWallQ5','tpWallN5','tpWallR5','tpWallQ6','tpWallN6','tpWallR6'], long:['tpWallTitle','tpWallQ1','tpWallQ2','tpWallQ3','tpWallQ4','tpWallQ5','tpWallQ6'] },
  { name: 'Emma spotlight', keys: ['emmaQuote','emmaName','emmaRole'], long:['emmaQuote'] },
  { name: 'Video grid', keys: ['tpGridKicker','tpGridTitle','tpGridSub','tpFilterAll','tpFilterWelsh','tpFilterSpanish','tpGroupCelebs','tpGroupLearners','tpEmpty'], long:['tpGridSub','tpEmpty'] },
  { name: 'Share your story', keys: ['tpShareKicker','tpShareTitle','tpShareBody','tpShareCta1','tpShareCta2','tpShareStep1Title','tpShareStep1Body','tpShareStep2Title','tpShareStep2Body','tpShareStep3Title','tpShareStep3Body'], long:['tpShareBody','tpShareStep1Body','tpShareStep2Body','tpShareStep3Body'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

export default function Testimonials() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });
  return (
    <div data-screen-label="Testimonials">
      <Header t={tweaks}/>
      <main>
        <TPHero t={tweaks}/>
        <TestimonialFeature t={tweaks}/>
        <IntensiveCrossLink variant="testimonials"/>
        <TPWrittenWall t={tweaks}/>
        <TPFilteredGrid t={tweaks}/>
        <WrittenStoriesD t={tweaks}/>
        <TPShareYourStory t={tweaks}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
